import { z } from 'zod';

// ============ Common field schemas ============

export const emailSchema = z.string().trim().email({ message: "Invalid email address" }).max(255);

export const passwordSchema = z.string().min(6, { message: "Password must be at least 6 characters" }).max(100);

export const urlSchema = z.string().url({ message: "Invalid URL" }).max(2048).or(z.literal(''));

export const phoneSchema = z.string().max(20).regex(/^[0-9+\-() ]*$/, { message: "Invalid phone number" }).or(z.literal(''));

// ============ Auth schemas ============

export const authSchema = z.object({
  email: emailSchema,
  password: passwordSchema,
});

export const resetPasswordSchema = z.object({
  newPassword: passwordSchema,
  confirmPassword: passwordSchema,
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: 'Passwords don\'t match',
  path: ["confirmPassword"],
});

// ============ Profile schemas ============

export const profileUpdateSchema = z.object({
  // Required name fields
  first_name: z.string().trim().min(1, 'First name is required').max(100, 'First name is too long'),
  last_name: z.string().trim().min(1, 'Last name is required').max(100, 'Last name is too long'),

  // Required contact fields
  phone: z.string().trim().min(1, 'Phone number is required').max(20, 'Phone number is too long')
    .regex(/^[0-9+\-() ]*$/, 'Invalid phone number'),
  country_code: z.string().trim().min(1, 'Country code is required').max(5).default('+1'),

  // Required address fields
  street_address: z.string().trim().min(1, 'Street address is required').max(200, 'Street address is too long'),
  city: z.string().trim().min(1, 'City is required').max(100, 'City is too long'),
  state: z.string().trim().min(1, 'State/Province is required').max(100, 'State is too long'),
  zip: z.string().trim().min(1, 'Zip/Postal code is required').max(20, 'Zip code is too long'),
  country: z.string().trim().min(1, 'Country is required').max(100, 'Country is too long'),

  // Optional address line 2
  street_address_line_2: z.string().max(200, 'Address line 2 is too long').optional().or(z.literal('')),

  // Required gender selection
  gender: z.enum(['male', 'female', 'other', 'prefer_not_to_say'], {
    errorMap: () => ({ message: 'Please select a gender' })
  }),

  // Required date of birth with validation
  date_of_birth: z.string()
    .trim()
    .min(1, 'Date of birth is required')
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format')
    .refine((date) => {
      const d = new Date(date);
      const now = new Date();
      const minDate = new Date('1900-01-01');
      return d >= minDate && d <= now;
    }, 'Date of birth must be between 1900 and today'),

  // Required language preference
  preferred_language: z.enum(['en', 'es', 'fr', 'de', 'pt', 'it'], {
    errorMap: () => ({ message: 'Please select a language' })
  }).default('en'),

  // Required group ID
  group_id: z.string().uuid('Invalid group ID'),

  // Optional subgroup (UUID when provided)
  subgroup_id: z.string().uuid('Invalid subgroup ID').optional().or(z.literal('')),

  // Optional avatar URL
  avatar_url: z.string().url('Invalid avatar URL').optional().or(z.literal('')),
});

export const passwordChangeSchema = z.object({
  newPassword: passwordSchema,
  confirmPassword: passwordSchema,
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: 'Passwords don\'t match',
  path: ["confirmPassword"],
});

// ============ Admin schemas ============

export const groupSchema = z.object({
  name: z.string().trim().min(1, { message: 'Name is required' }).max(100),
  slug: z.string().trim().min(1, { message: 'Slug is required' }).max(100).regex(/^[a-z0-9-]+$/, { message: 'Slug must be lowercase letters, numbers, and hyphens only' }),
  contact_name: z.string().max(100).optional(),
  contact_email: emailSchema.optional().or(z.literal('')),
  contact_phone: phoneSchema.optional(),
  city: z.string().max(100).optional(),
  state: z.string().max(100).optional(),
  country: z.string().max(100).optional(),
  website: urlSchema.optional(),
  logo_url: urlSchema.optional(),
  product_id: z.enum(['mindcotine', 'mindco-relief', 'all']).nullable().optional(),
});

export const subgroupSchema = z.object({
  name: z.string().trim().min(1, { message: 'Name is required' }).max(100),
  slug: z.string().trim().min(1, { message: 'Slug is required' }).max(100).regex(/^[a-z0-9-]+$/, { message: 'Slug must be lowercase letters, numbers, and hyphens only' }),
  description: z.string().max(500).optional(),
  group_id: z.string().uuid({ message: 'Valid group ID is required' }),
});

export const articleSchema = z.object({
  title: z.string().trim().min(1, { message: 'Title is required' }).max(200),
  content: z.string().min(1, { message: 'Content is required' }),
  excerpt: z.string().max(500).optional(),
  category: z.string().min(1),
  language: z.enum(['en', 'es', 'fr', 'de', 'pt', 'it']),
  published: z.boolean(),
  cover_image: urlSchema.nullable().optional(),
  product_id: z.enum(['mindcotine', 'mindco-relief', 'all']).nullable().optional(),
});

export const videoSchema = z.object({
  title: z.string().trim().min(1, { message: 'Title is required' }).max(200),
  description: z.string().max(2000).optional(),
  category: z.string().min(1),
  language: z.enum(['en', 'es', 'fr', 'de', 'pt', 'it']),
  published: z.boolean(),
  is_vr: z.boolean(),
  vr_projection: z.string().optional(),
  vr_stereo_mode: z.string().optional(),
  cover_image: urlSchema.nullable().optional(),
  product_id: z.enum(['mindcotine', 'mindco-relief', 'all']).nullable().optional(),
});

export const audioSchema = z.object({
  title: z.string().trim().min(1, { message: 'Title is required' }).max(200),
  description: z.string().max(2000).optional(),
  category: z.string().min(1),
  language: z.enum(['en', 'es', 'fr', 'de', 'pt', 'it']),
  published: z.boolean(),
  cover_image: urlSchema.nullable().optional(),
  product_id: z.string().nullable().optional(),
});

// ============ Type exports ============

export type AuthInput = z.infer<typeof authSchema>;
export type ResetPasswordInput = z.infer<typeof resetPasswordSchema>;
export type ProfileUpdateInput = z.infer<typeof profileUpdateSchema>;
export type PasswordChangeInput = z.infer<typeof passwordChangeSchema>;
export type GroupInput = z.infer<typeof groupSchema>;
export type SubgroupInput = z.infer<typeof subgroupSchema>;
export type ArticleInput = z.infer<typeof articleSchema>;
export type VideoInput = z.infer<typeof videoSchema>;
export type AudioInput = z.infer<typeof audioSchema>;
